import { evaluateTemplateValue } from '@ea-lab/reactive-json';

/**
 * Navigation générique vers une URL COMPOSÉE de plusieurs morceaux.
 *
 * Pourquoi une réaction (échappatoire config-first) : `redirectNow` (cœur
 * reactive-json) n'accepte qu'UNE valeur (`to`), évaluée telle quelle — il n'y a
 * aucune concaténation de chaîne dans le YAML (ni `setData`, ni
 * `setAttributeValue`, ni `simpleMapping` ne composent une chaîne). Or après un
 * `submitData` de CRÉATION, l'URL de destination n'existe nulle part : le back
 * renvoie l'id du nœud créé, pas l'URL front. Il faut donc assembler
 * `"/builder/dashboards/" + id + "?edit=1"` — la seule capacité manquante est la
 * concaténation, d'où cette réaction générique plutôt qu'un composant de page.
 *
 * Le contournement « lien caché + `setAttributeValue` en append + `triggerEvent`
 * click » NE MARCHE PAS ici : l'événement `response` est diffusé dans la
 * micro-tâche qui suit l'écriture des données, AVANT que React n'ait re-rendu —
 * le `href` cliqué serait encore l'ancien. Une réaction, elle, lit la réponse
 * directement dans l'événement (`<reactive-json:event-new-value>`), donc jamais
 * de valeur périmée.
 *
 * `to` : une chaîne, ou un TABLEAU de morceaux joints tels quels. Chaque morceau
 * est évalué comme une valeur reactive-json (`~~.foo` → donnée du store ; toute
 * autre chaîne reste littérale), et les placeholders d'événement sont déjà
 * résolus par le moteur avant l'appel.
 *
 * Garde-fou : si un morceau est vide / absent (`null`, `undefined`, `""`), on ne
 * navigue PAS — mieux vaut rester sur place que d'atterrir sur une URL amputée
 * (ex. `/builder/dashboards/?edit=1` si la création a échoué).
 *
 * Les morceaux sont joints VERBATIM (aucun encodage) : c'est ce qui permet d'y
 * mettre un chemin (`/builder/dashboards/`) et une query (`?edit=1`).
 *
 * `replace: true` remplace l'entrée d'historique courante au lieu d'en empiler
 * une (utile après une création : le retour arrière ne rejoue pas le formulaire).
 *
 * Usage YAML (rediriger vers le tableau qu'on vient de créer, en édition) :
 *   - { what: submitData, on: click, url: "/decoupled-api/save?…&bundle=dashboard",
 *       httpMethod: POST, data: { data: ~~.createData }, updateOnlyData: true,
 *       updateDataAtLocation: ~~._ack }
 *   - what: navigateTo
 *     on: response
 *     to: [ "/builder/dashboards/", "<reactive-json:event-new-value>.id", "?edit=1" ]
 *
 * Comme `redirectNow`, la navigation est un chargement de page complet (l'état
 * de l'application est réinitialisé).
 *
 * Enregistrée dans [App.jsx](../../../App.jsx) sous `customPlugins.reaction.navigateTo`.
 */
export const navigateToReaction = (props) => {
  const { args, globalDataContext, templateContext } = props;
  if (!args || args.to === undefined || args.to === null) return;

  const gdc = globalDataContext?.getRootContext
    ? globalDataContext.getRootContext()
    : globalDataContext;

  const parts = Array.isArray(args.to) ? args.to : [args.to];
  const resolved = [];
  for (const part of parts) {
    const value = evaluateTemplateValue({
      valueToEvaluate: part,
      globalDataContext: gdc,
      templateContext,
    });
    // Un morceau manquant = URL amputée : on abandonne la navigation.
    if (value === null || value === undefined || value === '') {
      console.warn('navigateTo: URL incomplète, navigation annulée.', args.to);
      return;
    }
    resolved.push(String(value));
  }

  const url = resolved.join('');
  if (args.replace === true) window.location.replace(url);
  else window.location.assign(url);
};
